import { v4 as uuid } from 'uuid'
import {
  compact,
  groupBy,
  isBoolean,
  isEmpty,
  isNil,
  omit,
  pick,
} from 'lodash'

import { getChangedValues } from 'utils/formik'
import {
  SETTINGS_LEVELS,
  TIP_CONFIGURATIONS_SETTINGS,
  TIP_OPTIONS_SETTINGS_NAMES,
  venueSettingsItems,
} from 'resources'
import env from 'utils/env'

/**
 * Retrieves all settings names
 */

export const getAllVenueSettingsNames = () => {
  const settingsNames = venueSettingsItems?.map(
    (venueSettingsItem) => venueSettingsItem.name
  )

  const tipConfigurationsSettingsNames =
    settingsUtils?.getAllTipConfigurationSettingsNames()

  return compact([
    ...settingsNames,
    ...tipConfigurationsSettingsNames,
  ])
}

/**
 * Returns only fields which have been changed
 *
 * @param {object} values all values from formik - both changed and not changed
 * @param {object} initialValues formik's initial values
 * @param {string} venueUuid current venue uuid
 * @param {object} extraValues extra values to add to each setting like standMenuId, standId, etc.
 *
 * @return {array} venueSettingsToSend only changed fields with corrensponding settings
 */

export const renderFieldsToCreateSettings = (
  values,
  initialValues,
  venueUuid,
  extraValues = {}
) => {
  // get only values which were changed and remove all unchanged values
  const filteredValues = getChangedValues(values, initialValues)

  // if enable_pos_sms_receipts is false we shouldn't update sms_receipt_message
  const filterSmsReceiptMessage = values?.enable_pos_sms_receipts
    ? filteredValues
    : omit(filteredValues, 'sms_receipt_message')

  const venueSettingsToSend = transformSettings(
    Object.entries(filterSmsReceiptMessage),
    venueUuid,
    extraValues
  )

  return venueSettingsToSend
}

/**
 * Transforms a list of settings, applying necessary conversions and adding additional metadata.
 *
 * @param {Array} settings - An array of settings where each setting is represented as a [key, value] pair.
 * @param {string} venueUuid - The unique identifier for the venue.
 * @param {Object} [extraValues={}] - An optional object containing additional values to include in each setting.
 * @returns {Array} - An array of transformed settings objects, each with added metadata.
 *
 * @example
 * const settings = [
 *   ['alcohol_limit_per_customer', '5'],
 * ];
 *
 * Example output:
 * [
 *   {
 *     name: 'alcohol_limit_per_customer',
 *     value: 5,
 *     externalRefId: 'generated-uuid',
 *     organizationName: 'OrgName',
 *     venueUuid: 'venueUuid',
 *     someExtraField: 'extraValue',
 *   },
 * ]
 */

export const transformSettings = (
  settings,
  venueUuid,
  extraValues = {}
) => {
  return settings.reduce((result, [key, value]) => {
    let convertedNumberValue
    // check if current iterated element is number field
    // we have to replace empty string with null
    if (
      [
        // uncomment when it this settings will be implemented
        // 'max_order_amount_in_dollars',
        'alcohol_limit_per_customer',
      ].includes(key)
    ) {
      // if value is empty string return null
      // if value is not empty convert this value to Number
      convertedNumberValue =
        value === '' || value === 0 ? null : Number(value)

      // if it is not number field, leave as it is
    } else {
      convertedNumberValue = value
    }

    // stringify booleans: true -> 'true', false -> 'false'
    const stringifiedValue = isBoolean(convertedNumberValue)
      ? convertedNumberValue.toString()
      : convertedNumberValue

    // when api return qr_tender as null we have to convert it to '#default_empty_value' in order to
    // display "Default" value in Select, and now we check if value is equal '#default_empty_value' if so we convert it to null
    const transformedDefulaValue =
      stringifiedValue === '#default_empty_value'
        ? null
        : stringifiedValue

    // add new value
    const settingObjectWithChangedValueWithAddedValue = {
      name: key,
      value: transformedDefulaValue,
      externalRefId: uuid(),
      organizationName: env.ORG_NAME,
      venueUuid,
      ...extraValues,
    }

    return [...result, settingObjectWithChangedValueWithAddedValue]
  }, [])
}

// when venue settings' value is boolean it is displayed as 'true' or 'false'
// here we replace those strings to acutal booleans
// 'true' => true
// 'false' => false
const transformVenueSettingsValues = (venueSettings) => {
  return venueSettings?.reduce((result, item) => {
    let value

    // value can be string: 'true' or 'false'
    // or array: ['on'] or ['off']
    // or array of numbers: [1, 2, 3]
    // or boolean: true or false
    // we have to convert it to boolean
    if (['false', 'true'].includes(item.value)) {
      value = !!(item.value === 'true')
    } else if (Array.isArray(item.value)) {
      if (item.value[0] === 'on' || item.value[0] === 'off') {
        value = !!(item.value[0] === 'on')
      } else {
        value = item.value
      }
    } else {
      value = item.value
    }

    return { ...result, [item.name]: value }
  }, {})
}

const validateSettingLevel = (levels) => {
  // return an object with the correct order of levels
  // from the most specific to the least specific
  const levelsInCorrectOrder = Object.keys(SETTINGS_LEVELS).reduce(
    (acc, level) => {
      const correctLevel = {
        [level]: levels[level],
      }

      if (level) {
        return { ...acc, ...correctLevel }
      }

      return acc
    },
    {}
  )

  // if the levels are all empty, return the default level
  if (
    Object.values(levelsInCorrectOrder).every((value) =>
      isEmpty(value)
    )
  ) {
    return 'DEFAULT'
  }

  // if all levels have value return the most specific level
  if (
    Object.values(levelsInCorrectOrder).every(
      (value) => !isEmpty(value)
    )
  ) {
    return SETTINGS_LEVELS.standMenuId
  }

  // find index of first non-empty element
  // since the levels are in the correct order
  // the first non-empty element will be the level we want
  const indexOfFirstNonEmptyElement = Object.values(
    levelsInCorrectOrder
  ).findIndex((value) => value !== null)

  // return the correct level based on the index of the first non-empty element
  const correctLevel = Object.keys(levelsInCorrectOrder)[
    indexOfFirstNonEmptyElement
  ]

  return SETTINGS_LEVELS[correctLevel]
}

// We can filter the settings by extra filters
// If no extra filters are provided, we return the settings as they are
const filterSettingsByExtraFilters = ({
  settings = [],
  extraFilters = {},
}) => {
  if (isEmpty(extraFilters)) {
    return settings
  }

  const settingsFilteredByExtraFilters = settings?.filter(
    (setting) => {
      return Object.entries(extraFilters).every(
        ([key, value]) => setting[key] === value
      )
    }
  )

  return settingsFilteredByExtraFilters
}

const filterSettingsByLevel = ({
  settings = [],
  level,
  standMenuId,
  standId,
  venueUuid,
  organizationName,
}) => {
  switch (level) {
    case SETTINGS_LEVELS.standMenuId:
      return settings.filter(
        (setting) =>
          setting.standMenuId === standMenuId &&
          setting.standId === standId &&
          setting.venueUuid === venueUuid &&
          setting.organizationName === organizationName
      )
    case SETTINGS_LEVELS.standId:
      return settings.filter(
        (setting) =>
          setting.standMenuId === null &&
          setting.standId === standId &&
          setting.venueUuid === venueUuid &&
          setting.organizationName === organizationName
      )
    case SETTINGS_LEVELS.venueUuid:
      return settings.filter(
        (setting) =>
          setting.standMenuId === null &&
          setting.standId === null &&
          setting.venueUuid === venueUuid &&
          setting.organizationName === organizationName
      )
    case SETTINGS_LEVELS.organizationName:
      return settings.filter(
        (setting) =>
          setting.standMenuId === null &&
          setting.standId === null &&
          setting.venueUuid === null &&
          setting.organizationName === organizationName
      )

    default:
      return []
  }
}

/**
 * Returns the settings with default settings based on the level.
 * Settings level chain:
 * 1. Stand menu ID
 * 2. Stand ID
 * 3. Venue UUID
 * 4. Organization name
 *
 * So if we provide venue UUID and organization name, the settings will be filtered only by these values.
 *
 * @param {Object} params - The function parameters.
 * @param {Array} params.settings - The settings array.
 * @param {Array} params.defaultSettings - The default settings array.
 * @param {string} params.standMenuId - The stand menu ID.
 * @param {string} params.standId - The stand ID.
 * @param {string} params.venueUuid - The venue UUID.
 * @param {string} params.organizationName - The organization name.
 * @param {Object} params.extraFilters - The extra filters for settings. This has to be an object. For example: { itemId: null } - it will filter the settings by the itemId: null.
 * @param {Array} params.settingsNames - The names of the settings.
 * @returns {Array} The settings with default settings.
 */

export const determineValidVenueSettingsLevel = ({
  settings = [],
  defaultSettings = [],
  standMenuId = null,
  standId = null,
  venueUuid = null,
  organizationName = null,
  extraFilters = {},
  settingsNames = getAllVenueSettingsNames(),
}) => {
  // validate if all levels are provideded
  const areAllLevelsEmpty = [
    standMenuId,
    standId,
    venueUuid,
    organizationName,
  ].every((level) => isEmpty(level))

  // validate if settings and defaultSettings are empty
  const areSettingsAndDefaultSettingsEmpty =
    isEmpty(settings) && isEmpty(defaultSettings)

  // if settings and defaultSettings are empty or all levels are empty, return an empty array
  if (areAllLevelsEmpty || areSettingsAndDefaultSettingsEmpty) {
    return []
  }

  const settingLevel = validateSettingLevel({
    standMenuId,
    standId,
    venueUuid,
    organizationName,
  })

  const settingsFilteredByExtraFilters = filterSettingsByExtraFilters(
    {
      settings,
      extraFilters,
    }
  )

  const settingsFilteredByLevel = filterSettingsByLevel({
    settings: settingsFilteredByExtraFilters,
    level: settingLevel,
    standMenuId,
    standId,
    venueUuid,
    organizationName,
  })

  const settingsWithDefaultSettings = settingsNames?.map((name) => {
    const venueSetting = settingsFilteredByLevel?.find(
      (setting) => setting.name === name
    )
    const defaultSetting = defaultSettings?.find(
      (setting) => setting.name === name
    )

    return {
      name,
      value: venueSetting?.value ?? defaultSetting?.value ?? null,
    }
  })

  return settingsWithDefaultSettings
}

/*
 * Returns settings from desired level, with propagation up to aboved levels.
 *
 * @param {Object} params - The function parameters.
 * @param {Array} params.settings - The settings array.
 * @param {Array} params.defaultSettings - The default settings array.
 * @param {string} params.standMenuId - The stand menu ID.
 * @param {string} params.standId - The stand ID.
 * @param {string} params.venueUuid - The venue UUID.
 * @param {string} params.organizationName - The organization name.
 * @param {Object} params.extraFilters - The extra filters for settings. This has to be an object. For example: { itemId: null } - it will filter the settings by the itemId: null.
 * @param {level} params.level - Desired level from which we want to get the settings. It can be one of the  SETTINGS_LEVELS_ORDER values. If level not provided it will filter from the lowest level - menu level
 *
 */

export const determineValidSettingsLevel = ({
  settings = [],
  defaultSettings = [],
  standMenuId = null,
  standId = null,
  venueUuid = null,
  organizationName = null,
  extraFilters = {},
  level = null,
  settingsNames = [],
}) => {
  const settingsFilteredByExtraFilters = filterSettingsByExtraFilters(
    {
      settings,
      extraFilters,
    }
  )

  const groupedSettingsByName = groupBy(
    settingsFilteredByExtraFilters,
    'name'
  )

  const filterSettingsByLevels = (settings) => {
    const menuSettingsCondition = (setting) => {
      return (
        standMenuId !== null &&
        standId !== null &&
        venueUuid !== null &&
        organizationName !== null &&
        setting.standMenuId === standMenuId &&
        setting.standId === standId &&
        setting.venueUuid === venueUuid &&
        setting.organizationName === organizationName
      )
    }
    const standSettingsCondition = (setting) =>
      standId !== null &&
      venueUuid !== null &&
      organizationName !== null &&
      setting.standMenuId === null &&
      setting.standId === standId &&
      setting.venueUuid === venueUuid &&
      setting.organizationName === organizationName

    const venueSettingsCondition = (setting) =>
      venueUuid !== null &&
      organizationName !== null &&
      setting.standMenuId === null &&
      setting.standId === null &&
      setting.venueUuid === venueUuid &&
      setting.organizationName === organizationName

    const organizationSettingsCondition = (setting) =>
      organizationName !== null &&
      setting.standMenuId === null &&
      setting.standId === null &&
      setting.venueUuid === null &&
      setting.organizationName === organizationName

    // settings filtered by each level, with proper hierarchy
    const menuSettings = settings.filter(menuSettingsCondition)
    const standSettings = settings.filter(standSettingsCondition)
    const venueSettings = settings.filter(venueSettingsCondition)
    const organizationSettings = settings.filter(
      organizationSettingsCondition
    )

    return [
      menuSettings.length ? menuSettings[0] : null,
      standSettings.length ? standSettings[0] : null,
      venueSettings.length ? venueSettings[0] : null,
      organizationSettings.length ? organizationSettings[0] : null,
    ]
  }

  const findFirstValidSetting = Object.values(
    groupedSettingsByName
  ).map((settings) => {
    const settingsByLevel = filterSettingsByLevels(settings)

    if (level) {
      return settingsByLevel
        .slice(level)
        .find((setting) => setting !== null)
    }

    return settingsByLevel.find((setting) => setting !== null)
  })

  const settingsWithDefaultSettings = settingsNames?.map((name) => {
    const venueSetting = findFirstValidSetting?.find(
      (setting) => setting?.name === name
    )
    const defaultSetting = defaultSettings?.find(
      (setting) => setting?.name === name
    )

    return {
      name,
      value: venueSetting?.value ?? defaultSetting?.value ?? null,
    }
  })

  return settingsWithDefaultSettings
}

export const convertSettingsToObject = (settings) =>
  settings?.reduce((acc, { name, value }) => {
    return {
      ...acc,
      [name]: value ?? null,
    }
  }, {})

/**
 * Check if provided level exists in the settings.
 *
 * To check if the specific level, we need to provide the settings, desire level, and required values appropriate for the desire level: standMenuId, standId, venueUuid, or organizationName.
 *
 * @param {Array} settings - The settings array.
 * @param {string} level - The level of the settings - available levels in resources/index.js: SETTINGS_LEVELS.
 * @param {string} [standMenuId = null] - The stand menu ID.
 * @param {string} [standId = null] - The stand ID.
 * @param {string} [venueUuid = null] - The venue UUID.
 * @param {string} [organizationName = null] - The organization name.
 * @returns {boolean}
 */

export const checkDesireSettingsLevel = ({
  settings,
  level,
  standMenuId = null,
  standId = null,
  venueUuid = null,
  organizationName = null,
}) => {
  const activeSettings = filterSettingsByExtraFilters({
    settings,
    extraFilters: { deactivatedAt: null },
  })

  const filteredSettings = filterSettingsByLevel({
    settings: activeSettings,
    level,
    standMenuId,
    standId,
    venueUuid,
    organizationName,
  })

  return filteredSettings.length > 0
}

/**
 * Retrieves all configuration settings names
 */

export const getAllTipConfigurationSettingsNames = () => {
  const tipConfigurationsSettingsNames =
    TIP_CONFIGURATIONS_SETTINGS.map((setting) => setting.name)

  const tipOptionsSettingsNames = TIP_OPTIONS_SETTINGS_NAMES

  return [
    ...tipConfigurationsSettingsNames,
    ...tipOptionsSettingsNames,
  ]
}

export const getSettingsForSpecificLevel = (settings, level) => {
  if (isEmpty(settings)) {
    return []
  }

  switch (level) {
    case SETTINGS_LEVELS.standMenuId:
      return settings.filter(
        (setting) =>
          !isNil(setting.standMenuId) &&
          !isNil(setting.standId) &&
          !isNil(setting.venueUuid) &&
          !isNil(setting.organizationName)
      )
    case SETTINGS_LEVELS.standId:
      return settings.filter(
        (setting) =>
          isNil(setting.standMenuId) &&
          !isNil(setting.standId) &&
          !isNil(setting.venueUuid) &&
          !isNil(setting.organizationName)
      )
    case SETTINGS_LEVELS.venueUuid:
      return settings.filter(
        (setting) =>
          isNil(setting.standMenuId) &&
          isNil(setting.standId) &&
          !isNil(setting.venueUuid) &&
          !isNil(setting.organizationName)
      )
    case SETTINGS_LEVELS.organizationName:
      return settings.filter(
        (setting) =>
          isNil(setting.standMenuId) &&
          isNil(setting.standId) &&
          isNil(setting.venueUuid) &&
          !isNil(setting.organizationName)
      )
    default:
      return []
  }
}

/**
 *
 * Return default tip configuration settings to send.
 *
 * @param {Object} initialSettings - The initial settings object.
 * @param {Array} processedSettingNames - The array of processed settings names.
 * @param {string} venueUuid - The venue uuid.
 * @param {Object} [extraValues={}] - The extra values to include in each setting.
 *
 * @returns {Array} - Default tip configuration settings to send.
 *
 * @description
 * - When creating or updating tip configuration settings, all tip configuration settings need to be sent,
 *   including those that have not been changed.
 *
 */

export const prepareDefaultTipConfigurationsToSend = (
  initialSettings,
  processedSettingNames,
  venueUuid,
  extraValues = {}
) => {
  const tipConfigurationSettingsNames =
    getAllTipConfigurationSettingsNames()

  // Extract initial tip configuration settings from initialSettings
  const initialTipConfigurations = pick(
    initialSettings,
    tipConfigurationSettingsNames
  )

  // Extract changed tip configuration settings
  const changedTipConfigurations = pick(
    initialTipConfigurations,
    processedSettingNames
  )

  // Omit settings that have been changed and should not be included
  const unchangedInitialConfigurations = omit(
    initialTipConfigurations,
    processedSettingNames
  )

  // If there are no changed tip configuration settings, return an empty array.
  // Otherwise, transform the unchanged initial tip configurations to the format suitable for sending.
  const transformedConfigurations =
    Object.keys(changedTipConfigurations).length > 0
      ? settingsUtils.transformSettings(
          Object.entries(unchangedInitialConfigurations),
          venueUuid,
          extraValues
        )
      : []

  return transformedConfigurations
}

const settingsUtils = {
  renderFieldsToCreateSettings,
  transformVenueSettingsValues,
  determineValidSettingsLevel,
  determineValidVenueSettingsLevel,
  convertSettingsToObject,
  checkDesireSettingsLevel,
  getAllTipConfigurationSettingsNames,
  getAllVenueSettingsNames,
  getSettingsForSpecificLevel,
  transformSettings,
  prepareDefaultTipConfigurationsToSend,
}

export default settingsUtils
